import {
  Building2,
  Contact,
  House,
  ClipboardList,
  ListChecks,
  Inbox,
  type LucideIcon,
} from "lucide-react";

import navigation from "./layout-30-navigation.json";
import type { MenuConfig, MenuItem } from "./types";
import { navigationAllowed } from "../lib/navigation-permissions";

export type Layout30Audience = keyof typeof navigation;

type NavigationItem = Omit<MenuItem, "children" | "icon"> & {
  icon?: string;
  permission?: string;
  permissionsAny?: string[];
  permissionSetsAny?: string[][];
};
type NavigationSection = { title: string; children: NavigationItem[] };

const icons: Record<string, LucideIcon> = {
  Building2,
  Contact,
  House,
  ClipboardList,
  ListChecks,
  Inbox,
};

function hasPermission(item: NavigationItem, permissions: Set<string>) {
  return navigationAllowed(item, permissions);
}

function toMenuItem(item: NavigationItem): MenuItem {
  const { icon, permission, permissionsAny, permissionSetsAny, ...menuItem } =
    item;
  void permission;
  void permissionsAny;
  void permissionSetsAny;
  const resolvedIcon = icon ? icons[icon] : undefined;
  if (icon && !resolvedIcon)
    throw new Error(`Unknown navigation icon: ${icon}`);
  return { ...menuItem, icon: resolvedIcon };
}

function toSections(
  sections: readonly NavigationSection[],
  permissions: Set<string>,
): MenuConfig {
  return sections.flatMap((section) => {
    const children = section.children
      .filter((item) => hasPermission(item, permissions))
      .map(toMenuItem);
    return children.length ? [{ title: section.title, children }] : [];
  });
}

export function getLayout30Menus(
  audience: Layout30Audience,
  effectivePermissions: readonly string[] = [],
) {
  const permissions = new Set(effectivePermissions);
  const definition = navigation[audience] as {
    primary: NavigationItem[];
    sections: NavigationSection[];
  };
  return {
    primary: definition.primary
      .filter((item) => hasPermission(item, permissions))
      .map(toMenuItem),
    sections: toSections(definition.sections, permissions),
  } satisfies { primary: MenuConfig; sections: MenuConfig };
}
